import { useMemo } from "react";
import { localizedIdentification } from "@/lib/localizedId";
import { idNative, idEndangered } from "@/lib/idCompat";
import { isUnidentified } from "@/lib/galleryUtils";

/**
 * Memoized tally of every metric the Gallery filter chips / mobile picker need.
 * Returns two objects:
 *  - `scoreboard`: raw counts (nullable while items are still loading)
 *  - `filterCounts`: filter-key-shaped map ready for the mobile filter popover
 *
 * Extracted from Gallery.jsx unchanged so behavior stays byte-for-byte
 * identical; only concern here is `identification.life_cycle`, `plant_type`,
 * keystone / endangered / native flags, and the tag / needs-id predicates.
 */
export default function useGalleryScoreboard(items, userLanguage) {
  const scoreboard = useMemo(() => {
    if (!items) return null;
    const tally = {
      total: items.length,
      native: 0, nonNative: 0,
      keystone: 0, endangered: 0,
      weed: 0, needsId: 0, beneficial: 0, untagged: 0,
      annual: 0, biennial: 0, perennial: 0,
    };
    for (const it of items) {
      const id = localizedIdentification(it, userLanguage) || {};
      const n = idNative(id);
      if (n === true) tally.native++;
      if (n === false) tally.nonNative++;
      if (id.is_keystone) tally.keystone++;
      if (idEndangered(id)) tally.endangered++;
      if ((id.plant_type || "").toLowerCase() === "weed") tally.weed++;
      if ((id.plant_type || "").toLowerCase() === "beneficial") tally.beneficial++;
      if (isUnidentified(it)) tally.needsId++;
      if (!Array.isArray(it.tags) || it.tags.length === 0) tally.untagged++;
      const lc = (id.life_cycle || "").toLowerCase();
      if (lc === "annual") tally.annual++;
      else if (lc === "biennial") tally.biennial++;
      else if (lc === "perennial") tally.perennial++;
    }
    return tally;
  }, [items, userLanguage]);

  const filterCounts = useMemo(() => {
    if (!scoreboard) return {};
    return {
      all: scoreboard.total,
      needs_id: scoreboard.needsId,
      untagged: scoreboard.untagged,
      native: scoreboard.native,
      non_native: scoreboard.nonNative,
      beneficial: scoreboard.beneficial,
      weed: scoreboard.weed,
      keystone: scoreboard.keystone,
      endangered: scoreboard.endangered,
      annual: scoreboard.annual,
      biennial: scoreboard.biennial,
      perennial: scoreboard.perennial,
    };
  }, [scoreboard]);

  return { scoreboard, filterCounts };
}
